// Enhanced ReviewPlan.tsx with recipe expansion
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardActions,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Badge
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { ThumbUp, ThumbDown, ExpandMore, Visibility, CheckCircle, Restaurant } from "@mui/icons-material";
import dayjs from "dayjs";
import api from "../../services/api";
import { API_BASE } from "../../config";   // Add this
import { getAuthToken } from "../../services/api";  // Add this
import { trackEvent, AnalyticsEvents } from "../../services/analytics";

interface RecipeDetail {
  recipe_id: number;
  name: string;
  description: string;
  image_url?: string;
  instructions?: string;
  ingredients?: Array<{name: string, quantity: number, unit: string}>;
  steps?: Array<{step_number: number, description: string}>;
  nutrition_info?: NutritionInfo;
  prep_time?: number;
  hasFullDetails?: boolean;
}

interface NutritionInfo {
  per_serving?: {
    calories?: number;
    protein_grams?: number;
    carbs_grams?: number;
    fat_grams?: number;
    fiber_grams?: number;
    sodium_mg?: number;
  };
  metadata?: {
    difficulty?: string;
    total_time_minutes?: number;
    prep_time_minutes?: number;
    cook_time_minutes?: number;
    cuisine_type?: string;
    course?: string;
    dietary_tags?: string[];
  };
}

// Component for displaying recipe metadata
const RecipeMetadata: React.FC<{ 
  nutrition?: NutritionInfo;
  servings?: number;
  prepTime?: number;
}> = ({ nutrition, servings, prepTime }) => {
  const perServing = nutrition?.per_serving;
  const metadata = nutrition?.metadata;
  
  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {/* Basic Info */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Serves: <strong>{servings || 'N/A'}</strong>
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Prep Time: <strong>{metadata?.prep_time_minutes || prepTime || 'N/A'} min</strong>
          </Typography>
          {metadata?.cook_time_minutes && (
            <Typography variant="subtitle2" color="text.secondary">
              Cook Time: <strong>{metadata.cook_time_minutes} min</strong>
            </Typography>
          )}
          {metadata?.difficulty && (
            <Typography variant="subtitle2" color="text.secondary">
              Difficulty: <strong>{metadata.difficulty}</strong>
            </Typography>
          )}
        </Box>
        
        {/* Nutrition Info */}
        {perServing && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Per Serving:</strong>
            </Typography>
            {perServing.calories && (
              <Typography variant="body2" color="text.secondary">
                Calories: <strong>{perServing.calories}</strong>
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {perServing.protein_grams && (
                <Typography variant="body2" color="text.secondary">
                  Protein: <strong>{perServing.protein_grams}g</strong>
                </Typography>
              )}
              {perServing.carbs_grams && (
                <Typography variant="body2" color="text.secondary">
                  Carbs: <strong>{perServing.carbs_grams}g</strong>
                </Typography>
              )}
              {perServing.fat_grams && (
                <Typography variant="body2" color="text.secondary">
                  Fat: <strong>{perServing.fat_grams}g</strong>
                </Typography>
              )}
            </Box>
            {perServing.fiber_grams && (
              <Typography variant="body2" color="text.secondary">
                Fiber: <strong>{perServing.fiber_grams}g</strong>
              </Typography>
            )}
          </Box>
        )}
      </Box>
      
      {/* Dietary Tags */}
      {metadata?.dietary_tags && metadata.dietary_tags.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {metadata.dietary_tags.map((tag, idx) => (
            <Chip 
              key={idx} 
              label={tag} 
              size="small" 
              variant="outlined"
              color="primary"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const ReviewPlan: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useAuth(); // Ensure user is authenticated
  const autoApprove = searchParams.get('autoApprove') === 'true';

  const [approvingPlan, setApprovingPlan] = useState(false);
  const [approvalProgress, setApprovalProgress] = useState<string | null>(null);
  const [plan, setPlan] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [replacingMeals, setReplacingMeals] = useState<Set<string>>(new Set());
  const [expandingRecipes, setExpandingRecipes] = useState<Set<number>>(new Set());
  const [expandedRecipes, setExpandedRecipes] = useState<Map<number, RecipeDetail>>(new Map());
  const [confirmDialog, setConfirmDialog] = useState<{open: boolean, mealId?: string, recipeId?: number}>({open: false});

  useEffect(() => {
    loadPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPlan = async (retryCount = 0) => {
    try {
      const { data } = await api.get(`/meal-plans/${id}`);
      setPlan(data);
      setLoading(false);
    } catch (err: any) {
      // Retry up to 3 times with exponential backoff if plan not found
      if (retryCount < 3 && (err.response?.status === 404 || err.response?.status === 500)) {
        console.log(`Plan not found, retrying... (attempt ${retryCount + 1}/3)`);
        setTimeout(() => loadPlan(retryCount + 1), 1000 * (retryCount + 1));
      } else {
        alert("Unable to load plan");
        setLoading(false);
      }
    }
  };

  const handleViewRecipe = async (recipeId: number, servings: number) => {
    if (expandedRecipes.has(recipeId)) {
      // Recipe already expanded, just show it
      return;
    }

    setExpandingRecipes(prev => new Set(prev).add(recipeId));

    try {
      // First check if recipe has details
      const { data: detailsData } = await api.get(`/recipes/${recipeId}/details`);
      
      if (detailsData.hasFullDetails) {
        // Recipe already has details, just display them
        setExpandedRecipes(prev => new Map(prev).set(recipeId, {
          ...detailsData.recipe,
          hasFullDetails: true
        }));
      } else {
        // Need to expand the recipe
        const { data: expandData } = await api.post(`/recipes/${recipeId}/expand`, { servings });
        setExpandedRecipes(prev => new Map(prev).set(recipeId, {
          ...expandData.recipe,
          hasFullDetails: true
        }));
      }
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to load recipe details");
    }

    setExpandingRecipes(prev => {
      const newSet = new Set(prev);
      newSet.delete(recipeId);
      return newSet;
    });
  };

  const handleLikeRecipe = async (recipeId: number) => {
    try {
      await api.post(`/recipes/${recipeId}/interaction`, { action: 'favorite' });
    } catch (err) {
      console.error('Failed to like recipe:', err);
    }
  };

  const handleRejectMeal = (mealId: string, recipeId: number) => {
    setConfirmDialog({ open: true, mealId, recipeId });
  };

  // Helper to perform the meal replacement
  const performMealReplacement = async (mealId: string, recipeId: number) => {
    const mealKey = `${plan.plan_id}-${mealId}`;
    setReplacingMeals(prev => new Set(prev).add(mealKey));
    setConfirmDialog({ open: false });

    try {
      const meal = plan.meals.find((m: any) =>
        `${m.assigned_date}-${m.meal_type}` === mealId
      );

      if (meal) {
        const response = await api.post(`/meal-plans/${id}/replace-meal`, {
          assigned_date: meal.assigned_date,
          meal_type: meal.meal_type,
          servings: meal.servings
        });

        setPlan((prevPlan: any) => ({
          ...prevPlan,
          meals: prevPlan.meals.map((m: any) =>
            `${m.assigned_date}-${m.meal_type}` === mealId
              ? { ...m, recipe: response.data.newRecipe }
              : m
          )
        }));

        // Remove expanded recipe data for the old recipe
        setExpandedRecipes(prev => {
          const newMap = new Map(prev);
          newMap.delete(recipeId);
          return newMap;
        });
      }
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to replace meal");
    }

    setReplacingMeals(prev => {
      const newSet = new Set(prev);
      newSet.delete(mealKey);
      return newSet;
    });
  };

  // Just replace the meal, no dislike recorded
  const handleJustReplace = async () => {
    const { mealId, recipeId } = confirmDialog;
    if (!mealId || !recipeId) return;
    await performMealReplacement(mealId, recipeId);
  };

  // Dislike the recipe AND replace the meal
  const handleDislikeAndReplace = async () => {
    const { mealId, recipeId } = confirmDialog;
    if (!mealId || !recipeId) return;

    try {
      await api.post(`/recipes/${recipeId}/interaction`, { action: 'dislike' });
    } catch (err: any) {
      // Continue even if dislike fails - the replacement is more important
      console.error("Failed to record dislike:", err);
    }

    await performMealReplacement(mealId, recipeId);
  };

  // Internal approval logic (called directly for auto-approve or after button click)
  const handleApproveInternal = async () => {
    setApprovingPlan(true);
    setApprovalProgress("Checking recipes...");

    try {
      const response = await api.post(`/meal-plans/${id}/approve`);

      // Listen for progress updates if the response includes a jobId
      if (response.data.jobId) {
        // Small delay to let SSE connect before first event fires
        await new Promise(resolve => setTimeout(resolve, 100));
        const es = new EventSource(`${API_BASE}/meal-plans/approval-progress/${response.data.jobId}?token=${getAuthToken()}`);

        es.onerror = () => console.error("Approval SSE error");

        es.onmessage = (ev) => {
          const evt = JSON.parse(ev.data);

          if (evt.stage === 'expanding') {
            setApprovalProgress(
              evt.recipeName
                ? `Expanding recipe: ${evt.recipeName}... (${evt.current}/${evt.total})`
                : `Expanding ${evt.total} recipes...`
            );
          }
          if (evt.stage === 'replacing_unsafe' || evt.stage === 'replaced') {
            setApprovalProgress(evt.message || "Checking dietary safety...");
          }
          if (evt.stage === 'fetching_images') {
            setApprovalProgress("Adding recipe images...");
          }
          if (evt.stage === 'done') {
            es.close();
            setApprovalProgress(null);
            setApprovingPlan(false);
            trackEvent(AnalyticsEvents.MEAL_PLAN_APPROVED, { plan_id: id });
            navigate("/dashboard");
          }
          if (evt.stage === 'error') {
            es.close();
            setApprovalProgress(null);
            setApprovingPlan(false);
            alert("Approval failed: " + evt.message);
          }
        };
      } else {
        // No expansion needed, direct approval
        setApprovalProgress(null);
        setApprovingPlan(false);
        trackEvent(AnalyticsEvents.MEAL_PLAN_APPROVED, { plan_id: id });
        navigate("/dashboard");
      }
    } catch (err: any) {
      setApprovalProgress(null);
      setApprovingPlan(false);

      // Check if membership required (403) - redirect to membership
      if (err.response?.status === 403) {
        localStorage.setItem('pendingApprovalPlanId', id!);
        navigate('/membership?from=approval');
        return;
      }

      alert(err.response?.data?.message || "Approval failed");
    }
  };

  // Button click handler - calls internal approval for members
  const handleApprove = () => {
    handleApproveInternal();
  };

  // Auto-approve after returning from membership page with subscription
  useEffect(() => {
    if (autoApprove && plan && plan.status === 'planned' && !approvingPlan) {
      // Clear the param to prevent re-triggering
      navigate(`/meal-plans/${id}/review`, { replace: true });
      // Small delay then auto-approve
      const timer = setTimeout(() => {
        handleApproveInternal();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApprove, plan?.status]); 

  const getMealTypeColor = (mealType: string) => {
    switch (mealType) {
      case 'breakfast': return 'primary';
      case 'lunch': return 'secondary';
      case 'dinner': return 'error';
      default: return 'default';
    }
  };

  const formatMealType = (mealType: string) => {
    return mealType.charAt(0).toUpperCase() + mealType.slice(1);
  };

  if (loading) {
    return (
      <Box mt={8} textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!plan) return <Typography>No plan found.</Typography>;

  const isPlanned = plan.status === "planned";

  // Helper to parse date without timezone conversion
  const parseLocalDate = (dateStr: string) => {
    const cleanDate = (dateStr || '').split('T')[0]; // Strip time/timezone
    return dayjs(cleanDate);
  };

  // Use actual_start_date if available, otherwise fall back to week_start_date
  const startDate = (plan.actual_start_date || plan.week_start_date || '').split('T')[0];
  const endDate = plan.actual_end_date?.split('T')[0] || parseLocalDate(plan.week_start_date).add(6, 'day').format('YYYY-MM-DD');

  // Calculate number of days (for partial weeks or full weeks)
  const numDays = parseLocalDate(endDate).diff(parseLocalDate(startDate), 'day') + 1;

  const mealsByDay = Array.from({ length: numDays }, (_, i) => {
    const date = parseLocalDate(startDate).add(i, "day");
    const dayMeals = plan.meals
      .filter((m: any) => {
        const mealDate = (m.assigned_date || '').split('T')[0];
        return mealDate === date.format('YYYY-MM-DD');
      })
      .sort((a: any, b: any) => {
        const order = { breakfast: 1, lunch: 2, dinner: 3 };
        return order[a.meal_type as keyof typeof order] - order[b.meal_type as keyof typeof order];
      });

    return {
      date,
      day: date.format("dddd"),
      meals: dayMeals
    };
  });

  return (
    <Box mt={6} px={3}>
      <Typography variant="h4" gutterBottom>
        {isPlanned ? "Review Plan" : "Meal Plan"} — week of{" "}
        {parseLocalDate(startDate).format("MMMM D, YYYY")}
      </Typography>

      {!isPlanned && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This plan is already <strong>{plan.status}</strong>. You can review the meals below.
        </Alert>
      )}

      {isPlanned && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Review each meal below. You can view full recipes, like recipes you want to see more often, or reject meals to get alternatives.
        </Alert>
      )}

      {/* Specific dietary violation alert - when AI couldn't fully resolve restrictions */}
      {plan.dietary_violations?.hasViolations && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Action Required:</strong> {plan.dietary_violations.message || 'Some recipes may contain ingredients that don\'t match your dietary restrictions.'} Please carefully review each recipe\'s ingredients before cooking.
        </Alert>
      )}

      {/* General allergy safety warning */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Important:</strong> While we check all recipes against your dietary restrictions, AI can make mistakes. If you or a family member has a food allergy, please always verify ingredients before cooking. When in doubt, make substitutions.
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {mealsByDay.map(({ date, day, meals }) => (
          <Box key={day}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {day}, {date.format("MMM D")}
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)'
              },
              gap: 2 
            }}>
              {meals.map((meal: any) => {
                const mealId = `${meal.assigned_date}-${meal.meal_type}`;
                const mealKey = `${plan.plan_id}-${mealId}`;
                const isReplacing = replacingMeals.has(mealKey);
                const isExpanding = expandingRecipes.has(meal.recipe.recipe_id);
                const expandedRecipe = expandedRecipes.get(meal.recipe.recipe_id);
                const isCompleted = meal.status === 'completed';
                const isLeftover = meal.is_leftover === true;

                return (
                  <Badge
                    key={mealId}
                    badgeContent={
                      isCompleted ? (
                        <CheckCircle
                          sx={{
                            color: 'success.main',
                            fontSize: 32,
                            bgcolor: 'white',
                            borderRadius: '50%'
                          }}
                        />
                      ) : null
                    }
                    sx={{
                      '& .MuiBadge-badge': {
                        right: 12,
                        top: 12,
                      }
                    }}
                  >
                    <Card
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        border: isCompleted ? '2px solid' : isLeftover ? '2px dashed' : '1px solid',
                        borderColor: isCompleted ? 'success.main' : isLeftover ? 'info.light' : 'divider',
                        overflow: 'hidden',
                      }}>
                      {/* Image container with overlays */}
                      <Box sx={{ position: 'relative', height: 240, overflow: 'hidden' }}>
                        {/* Background image or placeholder */}
                        {(meal.recipe.image_url || expandedRecipe?.image_url) ? (
                          <Box
                            component="img"
                            src={meal.recipe.image_url || expandedRecipe?.image_url}
                            alt={meal.recipe.name}
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              bgcolor: 'grey.200',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Restaurant sx={{ fontSize: 64, color: 'grey.400' }} />
                          </Box>
                        )}

                        {/* Top row: meal type chip + leftover chip */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            p: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box display="flex" gap={0.5}>
                            <Chip
                              label={formatMealType(meal.meal_type)}
                              color={getMealTypeColor(meal.meal_type) as any}
                              size="small"
                            />
                            {meal.is_leftover && (
                              <Chip
                                label="Leftover"
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(227, 242, 253, 0.9)',
                                  color: '#1565c0',
                                  fontWeight: 500,
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Bottom overlay with text */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                            p: 1.5,
                            pt: 4,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: 'white',
                              fontWeight: 700,
                              lineHeight: 1.2,
                              mb: 0.5,
                              textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                            }}
                          >
                            {meal.recipe.name}
                          </Typography>
                          <Box display="flex" gap={1.5} alignItems="center">
                            {isLeftover ? (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Reheat {meal.servings} serving{meal.servings !== 1 ? 's' : ''}
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  {meal.extra_servings > 0
                                    ? `${meal.servings + meal.extra_servings} servings (+${meal.extra_servings} leftovers)`
                                    : `${meal.servings} serving${meal.servings !== 1 ? 's' : ''}`}
                                </Typography>
                                {(meal.recipe.prep_time || expandedRecipe?.prep_time) && (
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    {meal.recipe.prep_time || expandedRecipe?.prep_time} min
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                          {/* Adaptation note */}
                          {(meal.notes || (meal.ingredient_overrides && meal.ingredient_overrides.length > 0)) && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#90caf9',
                                display: 'block',
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {meal.notes
                                ? `${meal.notes.split('. ')[0]}${meal.notes.includes('. ') ? '.' : ''}`
                                : 'Has ingredient adaptations'}
                            </Typography>
                          )}
                          {isCompleted && meal.completed_at && (
                            <Typography variant="caption" sx={{ color: '#81c784', display: 'block', mt: 0.25 }}>
                              Cooked {dayjs(meal.completed_at).format('MMM D')}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Recipe Details Accordion (below the image area) */}
                      {expandedRecipe && (
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle2">Recipe Details</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <RecipeMetadata
                              nutrition={expandedRecipe.nutrition_info}
                              servings={meal.servings}
                              prepTime={expandedRecipe.prep_time}
                            />

                            {expandedRecipe.instructions && (
                              <Box mb={2}>
                                <Typography variant="subtitle2" gutterBottom>Instructions:</Typography>
                                <Typography variant="body2">{expandedRecipe.instructions}</Typography>
                              </Box>
                            )}

                            {expandedRecipe.ingredients && (
                              <Box mb={2}>
                                <Typography variant="subtitle2" gutterBottom>Ingredients:</Typography>
                                <List dense>
                                  {expandedRecipe.ingredients.map((ing, idx) => (
                                    <ListItem key={idx} disablePadding>
                                      <ListItemText
                                        primary={`${ing.quantity} ${ing.unit} ${ing.name}`}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}

                            {expandedRecipe.steps && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>Cooking Steps:</Typography>
                                <List dense>
                                  {expandedRecipe.steps.map((step) => (
                                    <ListItem key={step.step_number} disablePadding>
                                      <ListItemText
                                        primary={`${step.step_number}. ${step.description}`}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      )}

                      <CardActions>
                        {isLeftover ? (
                          <Box width="100%" px={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Enjoy yesterday's dinner! No cooking required.
                            </Typography>
                          </Box>
                        ) : isReplacing ? (
                          <Box width="100%" textAlign="center">
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Finding alternative...
                            </Typography>
                            <LinearProgress />
                          </Box>
                        ) : (
                          <>
                            <Button
                              size="small"
                              startIcon={isExpanding ? <CircularProgress size={16} /> : <Visibility />}
                              onClick={() => handleViewRecipe(meal.recipe.recipe_id, meal.servings + (meal.extra_servings || 0))}
                              disabled={isExpanding}
                            >
                              {isExpanding ? "Loading..." : "View Recipe"}
                            </Button>

                            {isPlanned && (
                              <>
                                <Button
                                  size="small"
                                  startIcon={<ThumbUp />}
                                  onClick={() => handleLikeRecipe(meal.recipe.recipe_id)}
                                >
                                  Like
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<ThumbDown />}
                                  color="error"
                                  onClick={() => handleRejectMeal(mealId, meal.recipe.recipe_id)}
                                >
                                  Replace
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      </CardActions>
                    </Card>
                  </Badge>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      <Box mt={4} textAlign="center">
        {isPlanned ? (
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleApprove}
            disabled={replacingMeals.size > 0 || approvingPlan}
          >
            {approvingPlan ? approvalProgress || "Approving..." : "Approve & Continue"}
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={() => navigate("/dashboard")}
          >
            Back to Dashboard
          </Button>
        )}
      </Box>
      {approvalProgress && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {approvalProgress}
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({open: false})} maxWidth="sm" fullWidth>
        <DialogTitle>Replace This Meal?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Choose how you'd like to handle this meal:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">Just Replace</Typography>
              <Typography variant="body2" color="text.secondary">
                Find a different meal for this slot only. This recipe can still appear in future meal plans.
              </Typography>
            </Box>
            <Box sx={{ p: 2, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
              <Typography variant="subtitle1" fontWeight="600" color="error.main">Dislike & Replace</Typography>
              <Typography variant="body2" color="text.secondary">
                Mark this recipe as "disliked" so it won't appear in any future meal plans, and find a replacement.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDialog({open: false})}>Cancel</Button>
          <Button onClick={handleJustReplace} variant="outlined" color="primary">
            Just Replace
          </Button>
          <Button onClick={handleDislikeAndReplace} color="error" variant="contained">
            Dislike & Replace
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewPlan;